import type { U2000TableColumn } from '../../basic/U2000BasicTableList';
import type { U2000Condition } from '../../basic/U2000BasicSearch';
import { isCriteriaLookupAutocompleteOperator, resolveCriteriaLookup } from './U2000CriteriaLookupRegistry';
import {
  serializeTagsToCsv,
  serializeTagsToDisplayValue,
  type U2000CriteriaTag,
} from './u2000CriteriaTags';
import {
  type DateCriteriaRange,
  formatIntervalDisplayValue,
  isCriteriaDateColumn,
  isDateSingleValueOperator,
  isIntervalDateOperator,
  isIntervalRangeComplete,
  serializeIntervalValue,
} from './u2000DateCriteriaUtils';
import { isDatePresenceCriteria } from './U2000SearchPresenceCriteriaDisplay';
import { resolveStringCriteriaEditorMode } from './u2000StringCriteriaEditorUtils';

export interface U2000DraftCriteriaState {
  field: U2000TableColumn | null;
  condition: U2000Condition | null;
  value: string | null;
  displayValue?: string;
  lookupTags: U2000CriteriaTag[];
  intervalRange: DateCriteriaRange;
  calendarPickerValue?: { value: string; timestamp: number };
}

export interface U2000SerializedDraftCriteria {
  field: U2000TableColumn;
  condition: U2000Condition;
  value: string | null;
  displayValue?: string;
}

function hasCriteriaLookup(field: U2000TableColumn | null): boolean {
  return field?.criteriaLookup != null && resolveCriteriaLookup(field.criteriaLookup.findMethod) != null;
}

function isInOperator(condition: U2000Condition | null): boolean {
  return condition?.name === 'in';
}

function isLookupSingleOperator(condition: U2000Condition | null): boolean {
  return condition?.name === 'equals' || condition?.name === 'notequals';
}

function useLookupAutocomplete(field: U2000TableColumn | null, condition: U2000Condition | null): boolean {
  return hasCriteriaLookup(field) && isCriteriaLookupAutocompleteOperator(condition?.name);
}

function hasCriteriaInitOptions(field: U2000TableColumn | null): boolean {
  return field?.criteriaInitOptions != null;
}

function hasCriteriaStaticOptions(field: U2000TableColumn | null): boolean {
  return (field?.criteriaStaticOptions?.length ?? 0) > 0;
}

function hasCriteriaOptionsCombo(field: U2000TableColumn | null): boolean {
  return hasCriteriaInitOptions(field) || hasCriteriaStaticOptions(field);
}

function usesCriteriaComboDisplayValue(field: U2000TableColumn | null): boolean {
  return hasCriteriaOptionsCombo(field);
}

interface DraftCriteriaModes {
  lookupInMode: boolean;
  lookupSingleMode: boolean;
  comboInMode: boolean;
  comboSingleMode: boolean;
  stringInMode: boolean;
  showDateInterval: boolean;
  showDateSingle: boolean;
  dateSingleValue: string;
}

function resolveDraftCriteriaModes(state: U2000DraftCriteriaState): DraftCriteriaModes {
  const { field, condition, value, calendarPickerValue } = state;
  const lookupActive = useLookupAutocomplete(field, condition);
  const lookupInMode = lookupActive && isInOperator(condition);
  const lookupSingleMode = lookupActive && isLookupSingleOperator(condition);
  const comboInMode = hasCriteriaOptionsCombo(field) && isInOperator(condition);
  const comboSingleMode = hasCriteriaOptionsCombo(field) && isLookupSingleOperator(condition);
  const stringEditorMode = resolveStringCriteriaEditorMode(field, condition?.name);
  const stringInMode = stringEditorMode === 'in';
  const showDateInterval = isIntervalDateOperator(condition?.name);
  const showDateSingle = isCriteriaDateColumn(field) && isDateSingleValueOperator(condition?.name);
  const dateSingleValue = showDateSingle ? (value ?? calendarPickerValue?.value ?? '').trim() : '';

  return {
    lookupInMode,
    lookupSingleMode,
    comboInMode,
    comboSingleMode,
    stringInMode,
    showDateInterval,
    showDateSingle,
    dateSingleValue,
  };
}

/** True si au moins la colonne est renseignée (ligne non vide). */
export function hasDraftCriteriaContent(state: U2000DraftCriteriaState): boolean {
  return state.field != null;
}

/** Reprend la règle `isActionEnabled` du bouton Ajouter. */
export function isDraftCriteriaComplete(state: U2000DraftCriteriaState): boolean {
  const { field, condition, value, lookupTags, intervalRange } = state;
  if (field == null || condition == null) {
    return false;
  }

  const modes = resolveDraftCriteriaModes(state);

  if (!condition.isRequiredValue) {
    return true;
  }

  if (modes.lookupInMode || modes.comboInMode || modes.stringInMode) {
    return lookupTags.length > 0;
  }
  if (modes.showDateInterval) {
    return isIntervalRangeComplete(intervalRange);
  }
  if (modes.showDateSingle) {
    return modes.dateSingleValue.length > 0;
  }
  return value !== null && value !== '';
}

/** Sérialise le brouillon pour commit ; retourne null si incomplet ou invalide. */
export function serializeDraftCriteria(state: U2000DraftCriteriaState): U2000SerializedDraftCriteria | null {
  const { field, condition, value, displayValue, lookupTags, intervalRange } = state;
  if (field == null || condition == null || !isDraftCriteriaComplete(state)) {
    return null;
  }

  const modes = resolveDraftCriteriaModes(state);

  if (modes.lookupInMode || modes.comboInMode || modes.stringInMode) {
    if (condition.isRequiredValue && lookupTags.length === 0) {
      return null;
    }
    return {
      field,
      condition,
      value: serializeTagsToCsv(lookupTags),
      displayValue: serializeTagsToDisplayValue(lookupTags),
    };
  }

  if (isIntervalDateOperator(condition.name)) {
    if (!isIntervalRangeComplete(intervalRange)) {
      return null;
    }
    return {
      field,
      condition,
      value: serializeIntervalValue(intervalRange),
      displayValue: formatIntervalDisplayValue(intervalRange),
    };
  }

  if (modes.showDateSingle) {
    if (!modes.dateSingleValue) {
      return null;
    }
    return {
      field,
      condition,
      value: modes.dateSingleValue,
      displayValue: undefined,
    };
  }

  if (condition.isRequiredValue && (value === null || value === '')) {
    return null;
  }

  if (isDatePresenceCriteria(field.type, condition.name)) {
    return {
      field,
      condition,
      value: value ?? '',
      displayValue: undefined,
    };
  }

  const useDisplayValue = modes.lookupSingleMode || modes.comboSingleMode || usesCriteriaComboDisplayValue(field);
  return {
    field,
    condition,
    value,
    displayValue: useDisplayValue ? displayValue : undefined,
  };
}
